"use client"

import {useEffect, useRef} from "react";
import {useAppSelector} from "@/lib/hooks";
import {useIssueAppLoginCodeMutation} from "@/lib/api/auth/accountApi";
import {safeSessionStorage} from "@/lib/utils/storage";
import {APK_PROMPT_AFTER_REGISTRATION_KEY} from "@/lib/utils/contants/apkDownloadStorage";
import {isAndroidDevice, isApkDownloadEnabled, startApkDownload} from "@/lib/utils/apkDownload";

/**
 * Headless (AC-902): right after an email or phone registration on Android, starts
 * the APK download on its own — no button, no dialog.
 *
 * The APK carries the partner mark (`click` + `traffic` from localStorage) and,
 * per AC-903, a one-time login code so the app opens signed in. The code is
 * requested here, after `authenticate` has put the new session in place; if it
 * cannot be obtained the download still starts and the player signs in by hand.
 *
 * Runs once per registration: the session flag is consumed before anything else,
 * and the ref guards against StrictMode's double effect run.
 */
export default function ApkDownloadAfterRegistration() {
    const userId = useAppSelector(state => state.auth.userId);
    const [issueAppLoginCode] = useIssueAppLoginCodeMutation();
    const startedRef = useRef(false);

    useEffect(() => {
        if (!userId || startedRef.current) return;
        if (safeSessionStorage.getItem(APK_PROMPT_AFTER_REGISTRATION_KEY) !== 'true') return;

        startedRef.current = true;
        safeSessionStorage.removeItem(APK_PROMPT_AFTER_REGISTRATION_KEY);
        if (!isApkDownloadEnabled() || !isAndroidDevice()) return;

        void (async () => {
            let loginCode: string | undefined;
            try {
                loginCode = (await issueAppLoginCode().unwrap()).code;
            } catch {
                loginCode = undefined;
            }
            startApkDownload(loginCode);
        })();
    }, [userId, issueAppLoginCode]);

    return null;
}
